<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import AnchorPageObjectives from '../objectives/AnchorPageObjectives.vue'
import { useAnchorPageStore } from '../anchor_page.store'
import type { AnchorObjective } from '../anchor_page_types'
import type { ObjectiveLevel } from '../objectives/objective_levels'
import { useAnchorPageTracking } from '../useAnchorPageTracking'
import { useQuarterlyObjectiveResources } from './useQuarterlyObjectiveResources'

withDefaults(defineProps<{
  isReadOnly?: boolean
  readOnlyHint?: string
  levelsWithGeneratedAssessments?: ObjectiveLevel[]
  isLibraryCopy?: boolean
}>(), {
  isReadOnly: false,
  readOnlyHint: undefined,
  levelsWithGeneratedAssessments: () => [],
  isLibraryCopy: false
})

defineSlots<{
  generatedAssessments?(props: { objective: AnchorObjective, index: number }): unknown
}>()

const anchorPageStore = useAnchorPageStore()
const { content, customObjectives, accessInfo, actionPermissions, strategyMap, isFetchingCustomization } = storeToRefs(anchorPageStore)
const { editObjectiveText } = useAnchorPageTracking(getAnalyticsTracker())

const objectives = computed(() => content.value?.objectives ?? [])
const objectiveTexts = computed(() => customObjectives.value.map((objective) => objective.text))
const objectiveResourceUuids = computed(() => content.value?.objective_resources)
const assessmentItems = computed(() => content.value?.assessment_items)

const { resourcesByLevel, isFetching: isFetchingResources } = useQuarterlyObjectiveResources(objectiveResourceUuids)
</script>

<template>
  <div data-testid="quarterly-objectives">
    <h2 class="mb-6 mt-9 text-display-sm font-medium leading-tight">Quarterly Objectives</h2>
    <AnchorPageObjectives
      :objectives="objectives"
      :objective-texts="objectiveTexts"
      :items="assessmentItems"
      :page-uuid="content?.uuid"
      :access-info="accessInfo"
      :print-permissions="actionPermissions.printAssessment"
      :is-editable="isLibraryCopy || !accessInfo.blur"
      :is-fetching="isFetchingCustomization"
      :strategies="strategyMap"
      :objective-resource-uuids="objectiveResourceUuids"
      :resources-by-level="resourcesByLevel"
      :is-fetching-resources="isFetchingResources"
      :is-read-only="isReadOnly"
      :read-only-hint="readOnlyHint"
      :levels-with-generated-assessments="levelsWithGeneratedAssessments"
      @update:objective="editObjectiveText"
    >
      <template #generatedAssessments="slotProps">
        <slot name="generatedAssessments" v-bind="slotProps" />
      </template>
    </AnchorPageObjectives>
  </div>
</template>
